import { sum } from "lodash-es";
import { DateTime } from "luxon";
import React, { FC, useEffect } from "react";

import {
  Button,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";

import Header from "../../../components/Header";
import { pushAPIError } from "../../../slices/notifications";
import { useDispatch } from "../../../store";
import {
  Invoice,
  Organization,
  useCreateBillingPortalSessionMutation,
  useGetBillingUsageQuery,
  useGetSubscriptionInformationQuery,
  useListInvoicesQuery,
} from "../../../store/pomeriumApi";
import {
  buildResubscribeUrl,
  buildUpgradeUrl,
} from "../../billing/billing-urls";
import PaymentInformation from "./PaymentInformation";

type BillingHistoryProps = {
  organizationId?: string;
};

const BillingHistory: FC<BillingHistoryProps> = ({ organizationId }) => {
  const dispatch = useDispatch();
  const { data: invoices, error } = useListInvoicesQuery(organizationId || "", {
    skip: !organizationId,
  });

  useEffect(() => {
    if (error) {
      dispatch(pushAPIError(error));
    }
  }, [error]);

  const onDownload = (evt: React.MouseEvent, invoice: Invoice) => {
    evt.preventDefault();

    location.href = invoice.downloadUrl;
  };

  return (
    <Stack>
      <Typography variant="h4">Billing History</Typography>
      <TableContainer>
        <Table>
          <TableBody>
            {invoices?.map((invoice) => (
              <TableRow key={`${invoice.date}|${invoice.downloadUrl}`}>
                <TableCell>
                  {DateTime.fromISO(invoice.date).toLocaleString()}
                </TableCell>
                <TableCell>Invoice for {invoice.productName}</TableCell>
                <TableCell>
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: invoice.currency,
                  }).format(invoice.total / 100)}
                </TableCell>
                <TableCell align="right">
                  <Button onClick={(evt) => onDownload(evt, invoice)}>
                    Download
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

type SubscriptionDetailsProps = {
  organizationId?: string;
};

const SubscriptionDetails: FC<SubscriptionDetailsProps> = ({
  organizationId,
}) => {
  const dispatch = useDispatch();
  const [createBillingPortalSession] = useCreateBillingPortalSessionMutation();
  const { data: usage, error: getBillingUsageError } = useGetBillingUsageQuery(
    organizationId || "",
    {
      skip: !organizationId,
    },
  );
  const { data: subscription, error: getSubscriptionInformationError } =
    useGetSubscriptionInformationQuery(organizationId || "", {
      skip: !organizationId,
    });
  const total = sum([
    usage?.admins || 0,
    usage?.auditors || 0,
    usage?.members || 0,
    usage?.owners || 0,
    usage?.users || 0,
  ]);

  useEffect(() => {
    if (getBillingUsageError) {
      dispatch(pushAPIError(getBillingUsageError));
    }
    if (getSubscriptionInformationError) {
      dispatch(pushAPIError(getSubscriptionInformationError));
    }
  }, [getBillingUsageError, getSubscriptionInformationError]);

  const onCreateSubscription = (evt: React.MouseEvent) => {
    evt.preventDefault();
    location.href = subscription?.hasCustomerId
      ? buildResubscribeUrl(location.href)
      : buildUpgradeUrl(location.href);
  };

  const onManageSubscription = (evt: React.MouseEvent) => {
    evt.preventDefault();

    if (organizationId) {
      (async () => {
        const result = await createBillingPortalSession({
          createBillingPortalSessionRequest: {
            returnUrl: location.href,
            deepLink: "",
          },
          organizationId,
        });
        if ("data" in result && result.data) {
          location.href = result.data.url;
        }
      })();
    }
  };

  if (
    subscription?.status === "canceled" ||
    subscription?.status === "not_found"
  ) {
    return (
      <Stack>
        <Stack direction="row" justifyContent="space-between">
          <Stack>
            <Typography variant="h4">Subscription Details</Typography>
            <Typography>
              <Button onClick={onCreateSubscription}>
                Manage Subscription
              </Button>
            </Typography>
          </Stack>

          <Typography variant="caption">No Subscription Found</Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <Stack>
          <Typography variant="h4">Subscription Details</Typography>
          <Typography>
            <Button onClick={onManageSubscription}>Manage Subscription</Button>
          </Typography>
        </Stack>

        {subscription?.nextInvoiceDate && (
          <Typography variant="caption">
            Next invoice on{" "}
            {DateTime.fromISO(subscription?.nextInvoiceDate).toLocaleString()}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Stack>
          <Typography variant="h3">
            {subscription?.pricePerSeatPerMonthCents
              ? new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(subscription.pricePerSeatPerMonthCents / 100)
              : ""}
          </Typography>
          <Typography variant="caption">
            per seat per month (billed monthly)
          </Typography>
        </Stack>
        <Typography variant="h3">
          {total} seat{total === 1 ? "" : "s"}
        </Typography>
      </Stack>
      <Divider />
      <Stack direction="row" justifyContent="space-evenly">
        <Typography>Owners: {usage?.owners || 0}</Typography>
        <Typography>Admins: {usage?.admins || 0}</Typography>
        <Typography>Auditors: {usage?.auditors || 0}</Typography>
        <Typography>Members: {usage?.members || 0}</Typography>
        <Typography>Users: {usage?.users || 0}</Typography>
      </Stack>
      <Divider />
    </Stack>
  );
};

type BillingProps = {
  org: Partial<Organization>;
};
const Billing: FC<BillingProps> = ({ org }) => {
  return (
    <>
      <Header title="Billing" breadcrumbs={false} />
      <Stack spacing={3}>
        <SubscriptionDetails organizationId={org?.id} />
        <PaymentInformation organizationId={org?.id} />
        <BillingHistory organizationId={org?.id} />
      </Stack>
    </>
  );
};
export default Billing;
